import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import firebase from "gatsby-plugin-firebase";

// Initial state
const initialState = {
  recommendations: [],
  receivedRecs: [],
  isLoading: false,
  hasError: false,
  sentHasFulfilled: false,
  receivedHasFulfilled: false,
};

// Thunks
export const loadSentRecs = createAsyncThunk(
  "recommendations/getAllRecommendations",
  async () => {
    try {
      const querySnapshot = await firebase
        .firestore()
        .collection("referrals")
        .where("referrerUid", "==", firebase.auth().currentUser.uid)
        .get();
      if (querySnapshot.empty) {
        // User has not made any referrals yet
        return [];
      } else {
        return querySnapshot.docs.map((snapshot) => {
          return {
            id: snapshot.id,
            data: snapshot.data(),
          };
        });
      }
    } catch (error) {
      return error;
    }
  }
);

export const loadReceivedRecs = createAsyncThunk(
  "recommendations/getReceivedRecs",
  async () => {
    const recs = [];

    try {
      // Get recs from the referrals collection
      const recQuery = await firebase
        .firestore()
        .collection("referrals")
        .where("referredUid", "==", firebase.auth().currentUser.uid)
        .get();

      if (!recQuery.empty) {
        let referrer;
        for (const rec of recQuery.docs) {
          referrer = (
            await firebase
              .firestore()
              .collection("user-snippets")
              .doc(rec.data().referrerUid)
              .get()
          ).data();

          recs.push({
            id: rec.id,
            data: {
              firstName: referrer.firstName,
              lastName: referrer.lastName,
              photo: referrer.photo,
              username: referrer.username,
              referrerUid: rec.data().referrerUid,
              text: rec.data().referralText,
              status: rec.data().overallStatus,
            },
          });
        }

        return recs;
      } else {
        // There are no recs associated with this member
        return [];
      }
    } catch (error) {
      firebase.analytics().logEvent("loadReceivedRecs", {
        errorCode: error.code,
        errorMsg: error.message,
      });
      return null;
    }
  }
);

// Slice
export const recommendationsSlice = createSlice({
  name: "recommendations",
  initialState: initialState,
  reducers: {
    addRecommendation: (state, action) => {
      state.recommendations.push(action.payload);
    },
    updateRecommendation: (state, action) => {
      // spread action payload data across existing recommendation
      const index = state.recommendations.findIndex(
        (recommendation) => recommendation.id === action.payload.id
      );
      state.recommendations[index].data = {
        ...state.recommendations[index].data,
        ...action.payload.data,
      };
    },
    updateReceivedRec: (state, action) => {
      // spread action payload data across existing recommendation
      const index = state.receivedRecs.findIndex(
        (recommendation) => recommendation.id === action.payload.id
      );
      state.receivedRecs[index].data = {
        ...state.receivedRecs[index].data,
        ...action.payload.data,
      };
    },
  },
  extraReducers: {
    [loadSentRecs.pending]: (state, action) => {
      state.isLoading = true;
      state.hasError = false;
      state.sentHasFulfilled = false;
    },
    [loadSentRecs.fulfilled]: (state, action) => {
      state.recommendations = action.payload;
      state.isLoading = false;
      state.hasError = false;
      state.sentHasFulfilled = true;
    },
    [loadSentRecs.rejected]: (state, action) => {
      state.isLoading = false;
      state.hasError = true;
      state.sentHasFulfilled = false;
    },
    [loadReceivedRecs.fulfilled]: (state, action) => {
      if (action.payload !== null) {
        state.receivedRecs = action.payload;
      }
      state.receivedHasFulfilled = true;
    },
  },
});

// Selectors
export const selectRecommendations = (state) =>
  state.recommendations.recommendations;
export const selectReceivedRecs = (state) => state.recommendations.receivedRecs;
export const selectIsLoading = (state) => state.recommendations.isLoading;
export const selectHasError = (state) => state.recommendations.hasError;
export const selectSentHasFulfilled = (state) =>
  state.recommendations.sentHasFulfilled;
export const selectReceivedHasFulfilled = (state) =>
  state.recommendations.receivedHasFulfilled;
export const selectReferredUids = (state) => {
  // Return an array with all of the user's referredUids
  if (state.recommendations.recommendations.length !== 0) {
    return state.recommendations.recommendations.map((recommendation) => {
      if (recommendation.data.referredUid) {
        return recommendation.data.referredUid;
      } else {
        return null;
      }
    });
  } else {
    return [];
  }
};
export const selectReferredEmails = (state) => {
  // Return an array with all of the email addresses referred by the user
  if (state.recommendations.recommendations.length !== 0) {
    return state.recommendations.recommendations.map((recommendation) => {
      if (recommendation.data.email) {
        return recommendation.data.email;
      } else {
        return null;
      }
    });
  } else {
    return [];
  }
};

// Exports
export const { addRecommendation, updateRecommendation, updateReceivedRec } =
  recommendationsSlice.actions;
export default recommendationsSlice.reducer;
